import { useMutation } from '@base-app/web2'
import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_OFFERING, UPDATE_OFFERING } from '../../../graphql/offerings/mutations'
import { OFFERING_QUERY } from '../../../graphql/offerings/queries'
import type { Offering } from '../../../graphql/offerings/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { prependToLists } from '../../../lib/cacheLists'
import type { ImageDimensionRules } from '../../../lib/uploads'
import { lowResPathFor } from '../../../lib/uploads'
import { Button, FormActions } from '../../atoms'
import { FormField, FormLoadState, ImageUploadField, TextareaField } from '../../molecules'

// The offering image is a QR code, so it must be square
const QR_CODE_DIMENSIONS: ImageDimensionRules = { aspectRatio: { width: 1, height: 1 } }

interface OfferingFormFieldsProps {
  offering?: Offering
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

export const OfferingFormFields = memo(
  ({ offering, clientId, appId, onSuccess, onClose, skeleton }: OfferingFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const isEdit = !!offering
    const [title, setTitle] = useState(offering?.title ?? '')
    const [description, setDescription] = useState(offering?.description ?? '')
    const [textToCopy, setTextToCopy] = useState(offering?.text_to_copy ?? '')
    const [image, setImage] = useState(offering?.image ?? '')
    const [uploadingImage, setUploadingImage] = useState(false)
    const [observations, setObservations] = useState(offering?.observations ?? '')
    const [errors, setErrors] = useState<{ title?: string }>({})

    const makeOptimistic = useCallback(
      (id: string): Offering => ({
        __typename: 'Offering',
        id,
        client_id: clientId,
        application_id: appId,
        title,
        description: description || null,
        text_to_copy: textToCopy || null,
        image: image || null,
        image_low_res: lowResPathFor(image),
        observations: observations || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [clientId, appId, title, description, textToCopy, image, observations],
    )

    const [createOffering, { loading: creating }] = useMutation(CREATE_OFFERING, {
      optimisticResponse: { createOffering: makeOptimistic(`temp-${Date.now()}`) },
      update(cache, result) {
        const item = result.data?.createOffering
        if (!item) {
          return
        }
        prependToLists(cache, 'offerings', item, { clientId, applicationId: appId })
      },
      onError(err) {
        showToast(err.message, 'error')
      },
      onCompleted() {
        onSuccess()
      },
    })

    const [updateOffering, { loading: updating }] = useMutation(UPDATE_OFFERING, {
      optimisticResponse: { updateOffering: makeOptimistic(offering?.id ?? '') },
      onError(err) {
        showToast(err.message, 'error')
      },
      onCompleted() {
        onSuccess()
      },
    })

    const loading = creating || updating || uploadingImage

    const validate = useCallback(() => {
      const errs: { title?: string } = {}
      if (!title.trim()) {
        errs.title = t('common.form.titleRequired')
      }
      setErrors(errs)
      return Object.keys(errs).length === 0
    }, [title, t])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        const vars = {
          title,
          description: description || null,
          text_to_copy: textToCopy || null,
          image: image || null,
          observations: observations || null,
        }
        if (isEdit) {
          updateOffering({ variables: { id: offering.id, ...vars } })
        } else {
          createOffering({ variables: { client_id: clientId, application_id: appId, ...vars } })
        }
      },
      [
        validate,
        isEdit,
        offering?.id,
        title,
        description,
        textToCopy,
        image,
        observations,
        clientId,
        appId,
        updateOffering,
        createOffering,
      ],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        <FormField
          skeleton={skeleton}
          id="offering-title"
          label={t('offerings.form.titleLabel')}
          name="title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          error={errors.title}
          required
        />
        <FormField
          skeleton={skeleton}
          id="offering-text-to-copy"
          label={t('offerings.form.textToCopyLabel')}
          name="text_to_copy"
          value={textToCopy}
          onChange={e => setTextToCopy(e.target.value)}
          hint={t('offerings.form.textToCopyHint')}
        />
        <ImageUploadField
          skeleton={skeleton}
          applicationId={appId}
          id="offering-image-file"
          target="offerings"
          dimensions={QR_CODE_DIMENSIONS}
          label={t('offerings.form.imageLabel')}
          value={image}
          onChange={setImage}
          onUploadingChange={setUploadingImage}
        />
        <TextareaField
          skeleton={skeleton}
          id="offering-description"
          label={t('offerings.form.descriptionLabel')}
          name="description"
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
        <TextareaField
          skeleton={skeleton}
          id="offering-observations"
          label={t('offerings.form.observationsLabel')}
          name="observations"
          value={observations}
          onChange={e => setObservations(e.target.value)}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading ? t('offerings.form.saving') : t('offerings.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

OfferingFormFields.displayName = 'OfferingFormFields'

interface OfferingFormProps extends Omit<OfferingFormFieldsProps, 'offering'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const OfferingForm = memo(({ id, ...props }: OfferingFormProps) => {
  const { record, status } = useRecordById(OFFERING_QUERY, id, data => data.offering)
  if (status === 'loading') {
    return <OfferingFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <OfferingFormFields key={id ?? 'new'} offering={record} {...props} />
})

OfferingForm.displayName = 'OfferingForm'
